import { z } from "zod";
import { MeetingDoc } from "./docs.js";
import { AccountId } from "./ids.js";
/**
 * Google Meet launch (architecture §11). Meet and Calendar scopes are not part of the mail
 * connect: they are asked for incrementally, per Google account, the first time someone uses
 * Meet (`/oauth/google/start?purpose=meet&accountId=…`, see `meetConsentPath`).
 */
export const MEET_SCOPES = [
    "https://www.googleapis.com/auth/meetings.space.created",
    "https://www.googleapis.com/auth/calendar.events",
];
/** Whether a grant covers everything Meet needs (start now and schedule). */
export function hasMeetScopes(scopes) {
    return MEET_SCOPES.every((s) => scopes.includes(s));
}
/**
 * AI meeting notes (WP-017) read conference records and transcripts through the Meet API and
 * subscribe to the space's events through the Workspace Events API. `meetings.space.readonly`
 * (sensitive) covers both for every space the account can see, including the ones Calendar
 * creates for scheduled meetings (`meetings.space.created` only covers spaces our app created).
 * Meet consent asks for it together with `MEET_SCOPES`; a grant without it still launches Meet,
 * only without notes.
 */
export const MEET_NOTES_SCOPE = "https://www.googleapis.com/auth/meetings.space.readonly";
/** What the Meet consent asks for: launch plus notes. */
export const MEET_CONSENT_SCOPES = [...MEET_SCOPES, MEET_NOTES_SCOPE];
export function hasMeetNotesScope(scopes) {
    return scopes.includes(MEET_NOTES_SCOPE);
}
/** What the connect flow is for: a new mailbox, or the extra Meet scopes on an existing one. */
export const OAuthPurpose = z.enum(["connect", "meet"]);
/**
 * A path inside the web app (`/w/ws_1/chat/ch_1?x=1`): starts with one `/`, no backslash, no
 * scheme. The api also checks it resolves to WEB_ORIGIN before redirecting there.
 */
export const ReturnToPath = z
    .string()
    .max(500)
    .regex(/^\/(?![/\\])[^\\\s]*$/, "a same-origin path starting with /");
/** The api path (relative to the api origin) that starts Meet consent for one account. */
export function meetConsentPath(workspaceId, accountId, returnTo) {
    const q = new URLSearchParams({ workspaceId, accountId, purpose: "meet", returnTo });
    return `/oauth/google/start?${q.toString()}`;
}
const Title = z.string().trim().min(1).max(200);
const PostTo = z.object({ channelId: z.string().min(1) });
export const MeetStartNowInput = z.object({
    workspaceId: z.string().min(1),
    accountId: AccountId,
    title: Title,
    postTo: PostTo.optional(),
});
export const MeetScheduleInput = z
    .object({
    workspaceId: z.string().min(1),
    accountId: AccountId,
    title: Title,
    start: z.coerce.date(),
    end: z.coerce.date(),
    attendees: z.array(z.string().trim().toLowerCase().pipe(z.string().email())).max(100).default([]),
    postTo: PostTo.optional(),
})
    .refine((i) => i.end.getTime() > i.start.getTime(), { message: "end must be after start", path: ["end"] });
/** Meetings whose start falls in `[from, to)`. At most 62 days, so a month view always fits. */
export const MeetListInput = z
    .object({ workspaceId: z.string().min(1), from: z.coerce.date(), to: z.coerce.date() })
    .refine((i) => i.to.getTime() > i.from.getTime() && i.to.getTime() - i.from.getTime() <= 62 * 24 * 3600 * 1000, {
    message: "to must be after from, within 62 days",
    path: ["to"],
});
/** One meeting of a workspace, by id (the notes view, `kw meeting-notes`). */
export const MeetGetInput = z.object({ workspaceId: z.string().min(1), meetingId: z.string().min(1) });
export const MeetingView = MeetingDoc.extend({ id: z.string() });
export const MeetCreateOutput = z.object({ meeting: MeetingView, messageId: z.string().nullable() });
export const MeetListOutput = z.array(MeetingView);
//# sourceMappingURL=meet.js.map